import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

export interface MenuPermission {
  id: string;
  role: string;
  menuKey: string;
  menuName: string;
  menuIcon?: string;
  menuOrder: number;
  menuHref: string;
  canAccess: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
  active: boolean;
}

export interface MenuPermissionsResponse {
  success: boolean;
  data: MenuPermission[];
}

export const useMenuPermissions = () => {
  // console.log('🔍 DEBUG: useMenuPermissions inicializado');
  
  const [permissions, setPermissions] = useState<MenuPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  
  // console.log('🔍 DEBUG: useMenuPermissions state', { 
  // user: !!user, 
  // loading, 
  // permissionsCount: permissions.length 
  // });

  const fetchPermissions = async () => {
    // console.log('🔍 DEBUG: fetchPermissions chamado', { user: !!user });
    
    if (!user) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // console.log('🔍 DEBUG: Fazendo requisição para /menu-permissions/current', {
      //   userId: user.id,
      //   role: user.role,
      //   tenantId: user.tenantId || 'N/A'
      // });
      const response = await api.get<MenuPermissionsResponse>('/menu-permissions/current');
      console.log('🔍 DEBUG: Resposta das permissões da API', {
        success: response.data.success,
        count: response.data.data?.length || 0,
        menus: response.data.data?.map(m => ({ 
          key: m.menuKey, 
          name: m.menuName, 
          canAccess: m.canAccess, 
          active: m.active,
          role: m.role
        })),
        userRole: user?.role,
        userId: user?.id,
        tenantId: user?.tenantId
      });
      
      if (response.data.success) {
        console.log('🔍 DEBUG: Definindo permissões no estado', {
          count: response.data.data.length,
          permissions: response.data.data
        });
        setPermissions(response.data.data);
      } else {
        setError('Erro ao carregar permissões');
      }
    } catch (err: any) {
      console.error('Erro ao buscar permissões de menu:', err);
      const errorMessage = err?.response?.data?.message || 'Erro ao carregar permissões de menu';
      setError(errorMessage);
      // Para SUPER_ADMIN, não definir erro se for problema de tenant
      if (user?.role === 'SUPER_ADMIN' && errorMessage.includes('Tenant')) {
        console.warn('SUPER_ADMIN sem tenantId - tentando buscar permissões globais');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // SUPER_ADMIN pode não ter tenantId, mas ainda precisa carregar permissões
    // Outros roles precisam de tenantId
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';
    const canFetch = isSuperAdmin ? user?.id : (user?.id && user?.tenantId);
    
    if (canFetch) {
      fetchPermissions();
    } else if (user?.id && !isSuperAdmin && !user?.tenantId) {
      // Usuário não SUPER_ADMIN sem tenantId: não pode carregar permissões
      setPermissions([]);
      setLoading(false);
      setError('Tenant não identificado para este usuário');
    }
  }, [user?.id, user?.tenantId, user?.role]);

  // Função para verificar se o usuário pode acessar um menu
  const canAccessMenu = (menuKey: string): boolean => {
    const permission = permissions.find(p => p.menuKey === menuKey);
    return permission ? permission.canAccess : false;
  };

  // Função para verificar se o usuário pode criar em um menu
  const canCreateInMenu = (menuKey: string): boolean => {
    const permission = permissions.find(p => p.menuKey === menuKey);
    return permission ? permission.canCreate : false;
  };

  // Função para verificar se o usuário pode editar em um menu
  const canEditInMenu = (menuKey: string): boolean => {
    const permission = permissions.find(p => p.menuKey === menuKey);
    return permission ? permission.canEdit : false;
  };

  // Função para verificar se o usuário pode deletar em um menu
  const canDeleteInMenu = (menuKey: string): boolean => {
    const permission = permissions.find(p => p.menuKey === menuKey);
    return permission ? permission.canDelete : false;
  };

  // Função para verificar se o usuário pode visualizar em um menu
  const canViewInMenu = (menuKey: string): boolean => {
    const permission = permissions.find(p => p.menuKey === menuKey);
    return permission ? permission.canView : false;
  };

  // Mapeamento de nomes de menu para substituições visuais
  const menuNameMapping: { [key: string]: string } = {
    'Ordens de Serviço': 'Jobs/Tasks',
    'Ordem de Serviço': 'Job/Task',
    'ordens de serviço': 'jobs/tasks',
    'ordem de serviço': 'job/task'
  };

  // Função para obter o nome mapeado do menu
  const getMappedMenuName = (originalName: string): string => {
    return menuNameMapping[originalName] || originalName;
  };

  // Função para obter todos os menus que o usuário pode acessar
  const getAccessibleMenus = (): MenuPermission[] => {
    console.log('🔍 DEBUG: getAccessibleMenus chamado', {
      totalPermissions: permissions.length,
      permissions: permissions.map(p => ({
        menuKey: p.menuKey,
        canAccess: p.canAccess,
        active: p.active,
        menuOrder: p.menuOrder
      }))
    });
    
    const accessible = permissions
      .filter(p => p.canAccess && p.active)
      .sort((a, b) => a.menuOrder - b.menuOrder)
      .map(permission => ({
        ...permission,
        menuName: getMappedMenuName(permission.menuName)
      }));
    
    console.log('🔍 DEBUG: Menus acessíveis após filtro', {
      count: accessible.length,
      menus: accessible.map(m => ({ key: m.menuKey, name: m.menuName }))
    });
    
    return accessible;
  };

  // Função para obter permissões de um menu específico
  const getMenuPermissions = (menuKey: string): MenuPermission | null => {
    const permission = permissions.find(p => p.menuKey === menuKey);
    if (!permission) return null;
    
    return {
      ...permission,
      menuName: getMappedMenuName(permission.menuName)
    };
  };

  return {
    permissions,
    loading,
    error,
    canAccessMenu,
    canCreateInMenu,
    canEditInMenu,
    canDeleteInMenu,
    canViewInMenu,
    getAccessibleMenus,
    getMenuPermissions,
    getMappedMenuName,
    refetch: fetchPermissions
  };
};
